import { useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import gql from 'graphql-tag';

export const GET_REDIRECT_URL_QUERY = gql`
  query GetRedirectUrl(
    $userId: Int!
    $app: ArcadiaApp
    $path: String
    $queryParams: String
  ) {
    user(id: $userId) {
      id
      redirectUrl(app: $app, path: $path, queryParams: $queryParams)
    }
  }
`;

export const getRedirectUrlMock = {
  user: {
    id: 10,
    redirectUrl: 'http://localhost:3030/some/path',
  },
};

export const GET_ID_PARAMS = gql`
  query GetIdParams($planId: Int!) {
    plan(id: $planId) {
      id
      accountId
      utilityAccounts {
        id
      }
    }
  }
`;

export const getIdParamsMock = {
  plan: {
    id: 20,
    accountId: 1,
    utilityAccounts: [{ id: 5 }],
  },
};

interface FunnelRedirectProps {
  idParams: IdParams;
  userId: number;
  path?: string;
  reason: RedirectReason;
}
interface IdParams {
  accountId?: number;
  planId?: number;
  utilityAccountId?: number;
}

export const getParamString = ({
  accountId,
  planId,
  utilityAccountId,
}: IdParams): string => {
  const accountParam = accountId ? `&accountId=${accountId}` : '';
  const planParam = planId ? `&customPlanId=${planId}` : '';
  const utilityAccountParam = utilityAccountId
    ? `&utilityAccountId=${utilityAccountId}`
    : '';
  return `${accountParam}${planParam}${utilityAccountParam}`;
};

export enum RedirectReason {
  VnmAvailable = 'vnmAvailable',
  BillPayAvailable = 'billPayAvailable',
  UtilityConnectAvailable = 'utilityConnectAvailable',
  Moving = 'moving',
}

export const useFunnelRedirect = ({
  idParams,
  userId,
  path = '/dashboard',
  reason,
}: FunnelRedirectProps) => {
  // Note: If this query isn't being skipped, you'll need to pass the loading returned by this hook to the button using getData to prevent the user from calling getData before the utilityId query has returned. This is the current implementation pattern for this hook.
  const idParamsQuery = useQuery<GetIdParamsQuery, GetIdParamsQueryVariables>(
    GET_ID_PARAMS,
    {
      skip: !idParams.planId || !!idParams.utilityAccountId,
      variables: { planId: idParams.planId || -1 },
    }
  );

  const queriedPlan = idParamsQuery.data?.plan;
  const queriedUtilityAccountId = queriedPlan?.utilityAccounts[0]?.id;
  const queriedAccountId = queriedPlan?.accountId;

  const { accountId, planId, utilityAccountId } = idParams || {};

  const [getData, { data, loading, error }] = useLazyQuery(
    GET_REDIRECT_URL_QUERY,
    {
      variables: {
        app: 'funnel',
        path,
        queryParams: `reason=${reason}${getParamString({
          accountId: accountId || queriedAccountId || undefined,
          planId,
          utilityAccountId: utilityAccountId || queriedUtilityAccountId,
        })}`,
        userId,
      },
    }
  );

  useEffect(() => {
    if (data) window.location.href = data?.user.redirectUrl;
  }, [data]);

  return [
    getData,
    {
      error: error || idParamsQuery.error,
      loading: loading || idParamsQuery.loading || !!data,
    },
  ] as const;
};

export const useFunnelRedirectMock = [
  () => {
    window.location.href = getRedirectUrlMock.user.redirectUrl;
  },
  { error: null, loading: false },
];
